import * as React from 'react';

import { translate } from '~l10n/client';
import { IconInformationSvg } from '~shared/components/icons/large/IconInformation/IconInformationSvg';

import { CheckIcon, HourGlassIcon } from './Icons';

function TimerExpiredAlert() {
  return (
    <div className="timer-expired-alert" role="status" aria-live="polite">
      <div className="timer-expired-alert-icon" aria-hidden="true">
        <IconInformationSvg />
      </div>
      <p className="timer-expired-alert-text">{translate('Time expired')}</p>
    </div>
  );
}

/**
 * A component that is rendered for different activity types and different
 * states.
 */
export function StudentWaitView({
  isTeacherPaced,
  allQuestionsAnswered,
  isStudentFinished = false,
  timerExpired = false,
}: {
  isTeacherPaced: boolean;
  allQuestionsAnswered: boolean;
  isStudentFinished?: boolean;
  timerExpired?: boolean;
}) {
  if (isTeacherPaced && allQuestionsAnswered) {
    return <TeacherPacedCompleteView />;
  }

  if (isTeacherPaced && !allQuestionsAnswered) {
    return <TeacherPacedWaitView />;
  }

  if (allQuestionsAnswered || isStudentFinished) {
    // Student completed (either answered all or explicitly finished early)
    // Keep the completed message visible while waiting for the next activity.
    return <GeneralWaitView timerExpired={timerExpired} />;
  }

  return <GeneralCompleteView timerExpired={timerExpired} />;
}

/**
 * A component that is rendered when the student has answered all questions
 * in a teacher-paced activity. It displays a message indicating that the
 * student is waiting for the next question to be revealed.
 */
export function TeacherPacedWaitView() {
  return (
    <div className="waiting-for-teacher-container">
      <div className="waiting-inside-container">
        <div className="hex-loading-container">
          <HourGlassIcon />
        </div>
        <div className="waiting-text">
          <p className="already-responded-text" key="waitingText-p-1">
            {translate(
              'Answer submitted! Waiting for the next question to be revealed.'
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * A component that is rendered when the student has answered all questions
 * in a teacher-paced activity. It displays a message indicating that the
 * student is all done.
 */
export function TeacherPacedCompleteView() {
  return (
    <div className="waiting-for-teacher-container">
      <div className="waiting-inside-container">
        <div className="hex-loading-container">
          <CheckIcon />
        </div>
        <div className="waiting-text">
          <p className="already-responded-text" key="waitingText-p-1">
            {translate("You're all done!")}
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * A component that is rendered for other activity types other than
 * teacher-paced. It displays a message indicating that the student has finished
 * the current activity and is waiting for the next activity to begin.
 */
export function GeneralWaitView({
  timerExpired = false,
}: {
  timerExpired?: boolean;
}) {
  return (
    <div className="waiting-for-teacher-container">
      <div className="waiting-inside-container">
        {timerExpired && <TimerExpiredAlert />}
        <div className="hex-loading-container">
          <CheckIcon />
        </div>
        <div className="waiting-text">
          <p className="already-responded-text" key="waitingText-p-1">
            {translate('You‘ve completed the current activity.')}
          </p>
          <p key="waitingText-p-2" className="waiting-for-activity-text">
            {translate('Waiting for the next activity to begin…')}
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * A component that is rendered for other activity types other than
 * teacher-paced. It displays a message indicating that the activity is finished by teacher and student is
 * waiting for the next activity to begin.
 */
export function GeneralCompleteView({
  timerExpired = false,
}: {
  timerExpired?: boolean;
}) {
  return (
    <div className="waiting-for-teacher-container">
      <div className="waiting-inside-container">
        {timerExpired && <TimerExpiredAlert />}
        <div className="hex-loading-container">
          <HourGlassIcon />
        </div>
        <div className="waiting-text">
          <p key="waitingText-p-2" className="waiting-for-activity-text">
            {translate('Waiting for the next activity to begin…')}
          </p>
        </div>
      </div>
    </div>
  );
}
